import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  Copy,
  Check,
  Terminal,
  Key,
  Zap,
  ArrowRight,
  AlertTriangle,
  ChevronRight,
  Play,
  Telescope,
} from 'lucide-react';
import { useState, useMemo } from 'react';

import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ExecutionSummaryV2 } from '@/lib/types';
import { getExecutionTimestamp, getExecutionDuration } from '@/lib/execution-helpers';
// eslint-disable-next-line import-x/order
import { ReplayDialog } from '@/components/inspector/replay-dialog';

function timeAgo(iso: string): string {
  if (!iso) return '--';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '--';
  const diff = Date.now() - date.getTime();
  if (diff < 60000) return `${Math.floor(Math.max(0, diff) / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  return `${Math.floor(diff / 3600000)}h ago`;
}

function timeExact(iso: string): string {
  if (!iso) return '--:--:--';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '--:--:--';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function shortEntry(entry: string): string {
  const parts = entry.replace(/\.js$|\.ts$/, '').split(/[\/\\]/);
  return parts[parts.length - 1]!;
}

// ─── Tree builder ──────────────────────────────────────────────────────────

export interface ExecNode {
  exec: ExecutionSummaryV2;
  children: ExecNode[];
}

function buildExecutionTree(execs: ExecutionSummaryV2[]): ExecNode[] {
  const byRequestId = new Map<string, ExecutionSummaryV2>();
  for (const e of execs) {
    if (e.requestId) byRequestId.set(e.requestId, e);
  }
  const childrenOf = new Map<string, ExecutionSummaryV2[]>();
  const roots: ExecutionSummaryV2[] = [];
  for (const e of execs) {
    if (e.parentRequestId && byRequestId.has(e.parentRequestId)) {
      const arr = childrenOf.get(e.parentRequestId) ?? [];
      arr.push(e);
      childrenOf.set(e.parentRequestId, arr);
    } else {
      roots.push(e);
    }
  }
  function buildNode(exec: ExecutionSummaryV2): ExecNode {
    const children = exec.requestId ? (childrenOf.get(exec.requestId) ?? []) : [];
    children.sort((a, b): number => {
      const aAt = Date.parse(getExecutionTimestamp(a));
      const bAt = Date.parse(getExecutionTimestamp(b));
      return aAt - bAt;
    });
    return { exec, children: children.map(buildNode) };
  }

  roots.sort((a, b): number => {
    const aAt = Date.parse(getExecutionTimestamp(a));
    const bAt = Date.parse(getExecutionTimestamp(b));
    return bAt - aAt;
  });
  return roots.map(buildNode);
}

function CopySnippet({ text }: { text: string }): import('react/jsx-runtime').JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center justify-between rounded-md bg-[#0d1117] border border-white/10 px-3 py-2 font-mono text-sm">
      <span className="text-white/80">{text}</span>
      <button
        onClick={(): void => {
          void navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout((): void => setCopied(false), 2000);
        }}
        className="ml-3 shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-[var(--flux-success)]" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </button>
    </div>
  );
}

function getTraceMaxEnd(n: ExecNode): number {
  const receivedAt = Date.parse(getExecutionTimestamp(n.exec));
  let max = receivedAt + getExecutionDuration(n.exec);
  for (const c of n.children) {
    max = Math.max(max, getTraceMaxEnd(c));
  }
  return max;
}

interface Insight {
  type: 'cause' | 'critical' | 'anomaly' | 'pattern' | 'suggestion';
  text: string;
}

function inferErrorLabel(n: ExecNode): { label: string; suggestion: string } {
  const meta = n.exec.meta ?? {};
  const label = n.exec.preview?.label;

  if (label && label !== 'unknown error') {
    if (label.toLowerCase().includes('timeout'))
      return {
        label,
        suggestion: 'Check upstream service SLAs and add retry logic with exponential backoff',
      };
    if (label.toLowerCase().includes('rate limit'))
      return { label, suggestion: 'Implement request throttling or upgrade API tier' };
    if (label.toLowerCase().includes('auth'))
      return { label, suggestion: 'Verify API credentials and token expiry' };
    return { label, suggestion: 'Inspect service logs for the failing dependency' };
  }

  const providerKey = Object.keys(meta).find(
    (k): boolean => k.toLowerCase().includes('provider') || k.toLowerCase().includes('service'),
  );

  if (providerKey) {
    const val = meta[providerKey];
    return {
      label: `${String(val)} API timeout (external dependency)`,

      suggestion: `Check ${String(val)} API status page and configure a timeout + retry policy`,
    };
  }

  if (getExecutionDuration(n.exec) > 1500)
    return {
      label: 'External API latency exceeded threshold (high confidence)',
      suggestion: 'Add timeout guard to external call and alert on P95 latency',
    };

  if (getExecutionDuration(n.exec) > 800)
    return {
      label: 'External API latency exceeded threshold (moderate confidence)',
      suggestion: 'Profile this service call and review third-party dependency health',
    };

  return {
    label: 'Upstream dependency failure (low confidence — no metadata available)',
    suggestion: 'Enrich this function with metadata (provider, service) to improve diagnostics',
  };
}

function generateInsights(
  node: ExecNode,
  rootStatus: string | null | undefined,
): { insights: Insight[]; criticalPathNodeId: string | null } {
  const insights: Insight[] = [];
  const suggestions = new Set<string>();

  let criticalPathNode = node;
  let maxDuration = 0;

  const scan = (n: ExecNode, parentNode?: ExecNode): void => {
    const durationMs = getExecutionDuration(n.exec);
    if (durationMs > maxDuration && n !== node) {
      maxDuration = durationMs;
      criticalPathNode = n;
    }

    if (n.exec.status === 'error') {
      const { label, suggestion } = inferErrorLabel(n);
      const meta = n.exec.meta ?? {};
      const metaPairs = Object.entries(meta).filter(
        ([k]): boolean =>
          ![
            'http_method',
            'path',
            'projectId',
            'requestId',
            'parentRequestId',
            'executionId',
          ].includes(k),
      );

      let insight = `❌ ${shortEntry(n.exec.entry ?? 'unknown')} failed: ${label}`;
      if (metaPairs.length > 0) {
        insight +=
          `\n   - ` + metaPairs.map(([k, v]): string => `${k}: ${String(v)}`).join('\n   - ');
      }
      insights.push({ type: 'cause', text: insight });
      suggestions.add(suggestion);
    } else if (durationMs > 1500 && rootStatus === 'error') {
      insights.push({
        type: 'anomaly',
        text: `🐢 ${shortEntry(n.exec.entry ?? 'unknown')} slow (${durationMs}ms) — may be blocking upstream`,
      });
    }

    if (parentNode && durationMs > getExecutionDuration(parentNode.exec)) {
      insights.push({
        type: 'anomaly',
        text: `⚡ Async anomaly: ${shortEntry(n.exec.entry ?? 'unknown')} outlasted its parent (${durationMs}ms vs ${getExecutionDuration(parentNode.exec)}ms)`,
      });
    }

    n.children.forEach((c): void => scan(c, n));
  };

  scan(node);

  if (criticalPathNode != null && criticalPathNode !== node) {
    if (rootStatus === 'success') {
      insights.push({
        type: 'critical',
        text: `🔥 Longest operation: ${shortEntry(criticalPathNode.exec.entry ?? 'unknown')} (${getExecutionDuration(criticalPathNode.exec)}ms)`,
      });
    } else {
      insights.push({
        type: 'critical',
        text: `🔥 Critical path bottleneck: ${shortEntry(criticalPathNode.exec.entry ?? 'unknown')} (${getExecutionDuration(criticalPathNode.exec)}ms)`,
      });
    }
  }

  if (suggestions.size > 0) {
    insights.push({ type: 'suggestion', text: `💡 Suggested action: ${[...suggestions][0]}` });
  }

  const rank: Record<Insight['type'], number> = {
    cause: 1,
    pattern: 2,
    critical: 3,
    anomaly: 4,
    suggestion: 5,
  };
  const unique = Array.from(new Map(insights.map((i): [string, Insight] => [i.text, i])).values());

  return {
    insights: unique.sort((a, b): number => rank[a.type] - rank[b.type]).slice(0, 6),
    criticalPathNodeId:
      criticalPathNode !== node ? (criticalPathNode.exec.executionId ?? null) : null,
  };
}

function TraceTimelineNode({
  node,
  rootStartedAt,
  maxDurationMs,
  depth = 0,
  traceHasError = false,
  criticalPathNodeId,
}: {
  node: ExecNode;
  rootStartedAt: number;
  maxDurationMs: number;
  depth?: number | undefined;
  traceHasError?: boolean | undefined;
  criticalPathNodeId?: string | null | undefined;
}): import('react/jsx-runtime').JSX.Element {
  const execution = node.exec;
  const isError = execution.status === 'error';
  const durationMs = getExecutionDuration(execution);
  const isSlow = durationMs > 1500;
  const receiveOffsetMs = Math.max(0, Date.parse(getExecutionTimestamp(execution)) - rootStartedAt);

  const leftPercent = maxDurationMs > 0 ? (receiveOffsetMs / maxDurationMs) * 100 : 0;
  const widthPercent = maxDurationMs > 0 ? Math.max(0.5, (durationMs / maxDurationMs) * 100) : 100;

  const isCritical =
    execution.executionId === criticalPathNodeId && execution.executionId !== node.exec.executionId;

  return (
    <div className="relative flex flex-col font-mono text-[11px]">
      <div
        className={cn(
          'group relative flex items-center pr-4 py-1.5 transition-all rounded-[4px]',
          isError
            ? 'bg-[var(--flux-error)]/10 border border-[var(--flux-error)]/40 shadow-[0_0_15px_var(--flux-error)]/[0.1] z-20'
            : 'hover:bg-white/[0.03] border border-transparent',
          isCritical && 'bg-yellow-500/[0.08] border border-yellow-500/20 z-10',
          traceHasError && !isError && !isCritical && 'opacity-40 hover:opacity-100',
        )}
      >
        {depth > 0 && !isError && (
          <div
            className="absolute left-0 top-0 bottom-1/2 w-[11px] border-l border-b border-white/20 rounded-bl-[4px]"
            style={{ marginLeft: `${(depth - 1) * 16 + 15}px`, marginTop: '-8px' }}
          />
        )}

        <div
          className="flex items-center w-[200px] shrink-0 min-w-0"
          style={{ paddingLeft: `${depth * 16 + 15}px` }}
        >
          <Link
            to={`/projects/${execution.projectId ?? ''}/executions/${execution.executionId ?? ''}`}
            className="absolute inset-0 z-0"
            data-testid={`execution-link-${execution.executionId ?? ''}`}
          />
          <span
            className={cn(
              'relative z-10 font-bold truncate',
              isError
                ? 'text-[var(--flux-error)] text-xs'
                : isCritical
                  ? 'text-[var(--flux-warning)]'
                  : 'text-foreground/90',
            )}
          >
            {shortEntry(execution.entry ?? 'unknown')}
          </span>
        </div>

        <div className="flex-1 relative h-[22px] border-l border-r border-white/5 flex items-center mx-4 group-hover:border-white/10 transition-colors z-10">
          <div className="absolute inset-0 grid grid-cols-4 pointer-events-none">
            {(Array(4) as unknown[]).map((_, i): import('react/jsx-runtime').JSX.Element => (
              <div key={i} className="border-r border-white/[0.02]" />
            ))}
          </div>

          <div
            className={cn(
              'absolute rounded-sm shadow-[0_0_8px_currentColor] transition-all overflow-hidden',
              isCritical ? 'h-3.5 -mt-[1px] shadow-[0_0_20px_currentColor] z-30' : 'h-2',
              isError
                ? 'bg-[var(--flux-error)] shadow-[var(--flux-error)]/30'
                : isSlow
                  ? 'bg-orange-500 shadow-orange-500/20'
                  : isCritical
                    ? 'bg-[var(--flux-warning)] shadow-[var(--flux-warning)]/40'
                    : 'bg-white/40 shadow-white/5',
            )}
            style={{
              left: `${Math.min(99.5, leftPercent)}%`,
              width: `${Math.min(100 - leftPercent, widthPercent)}%`,
            }}
          >
            {isError && (
              <div className="absolute right-0 top-0 bottom-0 w-1.5 bg-red-400 animate-pulse border-l border-white/50" />
            )}
          </div>
        </div>

        <div className="w-[180px] shrink-0 truncate flex items-center text-[10px] z-10 pointer-events-none">
          {execution.preview ? (
            <span
              className={cn(
                'truncate',
                isError ? 'text-[var(--flux-error)] font-medium' : 'text-muted-foreground/80',
              )}
            >
              {execution.preview.label}
            </span>
          ) : execution.meta ? (
            <span className="truncate text-muted-foreground/80 font-medium">
              {Object.entries(execution.meta)
                .filter(
                  ([k]): boolean =>
                    ![
                      'http_method',
                      'path',
                      'projectId',
                      'requestId',
                      'parentRequestId',
                      'executionId',
                    ].includes(k),
                )
                .map(([k, v]): string => `${k}: ${String(v)}`)
                .slice(0, 2)
                .join(' • ')}
            </span>
          ) : null}
        </div>

        <div className="w-[80px] shrink-0 flex items-center justify-end gap-3 z-10 pointer-events-none">
          <span
            className={cn(
              'font-semibold text-[11px]',
              isError
                ? 'text-[var(--flux-error)]'
                : isSlow
                  ? 'text-[var(--flux-warning)]'
                  : 'text-foreground/70',
            )}
          >
            {durationMs}ms
          </span>
          {isError ? (
            <XCircle className="h-4 w-4 text-[var(--flux-error)]" />
          ) : (
            <CheckCircle2 className="h-3.5 w-3.5 text-[var(--flux-success)] opacity-40 shrink-0" />
          )}
        </div>
      </div>

      {node.children.length > 0 && (
        <div className="relative">
          <div
            className="absolute left-0 top-0 bottom-4 w-px bg-white/10"
            style={{ marginLeft: `${depth * 16 + 15}px`, zIndex: 0 }}
          />
          {node.children.map((c): import('react/jsx-runtime').JSX.Element => (
            <TraceTimelineNode
              key={c.exec.executionId ?? ''}
              node={c}
              rootStartedAt={rootStartedAt}
              maxDurationMs={maxDurationMs}
              depth={depth + 1}
              traceHasError={traceHasError}
              criticalPathNodeId={criticalPathNodeId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function TraceCard({
  rootNode,
  ongoingErrorPatterns = [],
  onFindSimilar: _onFindSimilar,
  onReplay,
}: {
  rootNode: ExecNode;
  ongoingErrorPatterns?: string[];
  onFindSimilar?: () => void;
  onReplay?: (exec: ExecutionSummaryV2) => void;
}): import('react/jsx-runtime').JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const root = rootNode.exec;
  const isError = root.status === 'error';

  const rootReceivedAt = Date.parse(getExecutionTimestamp(root));
  const traceEndAt = getTraceMaxEnd(rootNode);
  const maxDurationMs = Math.max(1, traceEndAt - rootReceivedAt);

  const { insights: generatedInsights, criticalPathNodeId } = useMemo(
    () => generateInsights(rootNode, root.status),
    [rootNode, root.status],
  );
  const isOngoingIssue = isError && ongoingErrorPatterns.includes(root.entry ?? '');
  const ongoingErrorCount = ongoingErrorPatterns.filter(
    (p): boolean => p === (root.entry ?? ''),
  ).length;

  const insights = useMemo((): Insight[] => {
    if (!isOngoingIssue) return generatedInsights;
    const patternText = `🚨 Related issue: ~${ongoingErrorCount} similar failure(s) detected cluster-wide — this trace is part of an ongoing pattern`;
    return [{ type: 'pattern', text: patternText } as Insight, ...generatedInsights];
  }, [generatedInsights, isOngoingIssue, ongoingErrorCount]);

  const hasErrorInside =
    root.status === 'error' || generatedInsights.some((i): boolean => i.type === 'cause');

  const requestStory = useMemo((): string => {
    const childNames = rootNode.children.map((c): string => shortEntry(c.exec.entry ?? 'unknown'));
    const errorChild = rootNode.children.find((c): boolean => c.exec.status === 'error');

    if (root.status === 'success') {
      if (childNames.length === 0)
        return `${shortEntry(root.entry ?? 'unknown')} completed with no downstream calls.`;
      if (childNames.length === 1)
        return `${shortEntry(root.entry ?? 'unknown')} completed successfully → called ${childNames[0]}.`;
      return `${shortEntry(root.entry ?? 'unknown')} completed, cascading across ${childNames.slice(0, 2).join(' + ')}${childNames.length > 2 ? ` and ${childNames.length - 2} more` : ''}.`;
    } else {
      const causeInsight = generatedInsights.find((i): boolean => i.type === 'cause');
      let rootCause = 'an infrastructure issue';
      if (causeInsight) {
        const match = causeInsight.text.match(/failed: ([^\n]+)/);
        if (match) rootCause = match[1]!.split(' (')[0]!;
      }
      if (errorChild) {
        return `${shortEntry(root.entry ?? 'unknown')} →  ${shortEntry(errorChild.exec.entry ?? '?')} failed due to: ${rootCause.toLowerCase()}.`;
      }
      return `${shortEntry(root.entry ?? 'unknown')} failed — cause: ${rootCause.toLowerCase()}.`;
    }
  }, [rootNode, root.entry, root.status, generatedInsights]);

  return (
    <div
      className={cn(
        'bg-[#111318] border rounded-lg overflow-hidden transition-all shadow-lg group',
        isError || hasErrorInside
          ? 'border-[var(--flux-error)]/30'
          : 'border-white/10 hover:border-white/20',
      )}
    >
      <div
        className={cn(
          'relative flex items-center gap-4 px-4 py-2 cursor-pointer backdrop-blur-sm',
          isError || hasErrorInside
            ? 'bg-[var(--flux-error)]/[0.02]'
            : 'bg-white/[0.01] hover:bg-white/[0.03]',
        )}
        onClick={(): void => setExpanded((v): boolean => !v)}
      >
        <div className="absolute inset-y-0 left-0 w-1 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />

        <div className="relative z-10 flex items-center gap-3 w-[260px] shrink-0 min-w-0">
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground/40 transition-transform duration-200',
              expanded && 'rotate-90',
            )}
          />
          <div className="flex flex-col min-w-0">
            <span className="font-bold text-sm truncate text-foreground">
              {root.meta?.['http_method'] != null && root.meta?.['path'] != null
                ? `${root.meta['http_method'] as string} ${root.meta['path'] as string}`
                : shortEntry(root.entry ?? 'unknown')}
            </span>
            <span className="font-mono text-[9px] text-muted-foreground/50 truncate mt-[1px]">
              {root.meta?.['http_method'] != null && root.meta?.['path'] != null
                ? shortEntry(root.entry ?? 'unknown')
                : (root.requestId ?? (root.executionId ? root.executionId.slice(0, 8) : 'unknown'))}
            </span>
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-4 shrink-0">
          <span
            className={cn(
              'px-2 py-[2px] rounded-[4px] bg-[#0d1117] border text-[9px] font-mono tracking-wider uppercase flex items-center justify-center min-w-[65px]',
              isError || hasErrorInside
                ? 'border-[var(--flux-error)]/30 text-[var(--flux-error)] shadow-[0_0_10px_var(--flux-error)]/[0.1]'
                : 'border-[var(--flux-success)]/30 text-[var(--flux-success)] shadow-[0_0_10px_var(--flux-success)]/[0.1]',
            )}
          >
            {isError || hasErrorInside ? 'ERROR' : 'SUCCESS'}
          </span>
          <span className="font-mono text-[13px] font-medium text-foreground/80">
            {getExecutionDuration(root)}ms
          </span>
        </div>

        <div className="relative z-10 flex-1 flex justify-end items-center gap-3 ml-3 pr-4">
          <div className="hidden group-hover:flex items-center gap-2 animate-in fade-in slide-in-from-right-2 duration-200">
            <Link
              to={`/projects/${root.projectId}/executions/${root.executionId}`}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-[6px] bg-white/[0.05] hover:bg-white/[0.1] text-[10px] font-mono uppercase text-foreground transition-all border border-white/10 shadow-lg"
              data-testid={`execution-inspect-${root.executionId}`}
            >
              <Telescope className="h-3 w-3 text-[var(--flux-accent)]" /> Inspect
            </Link>
            <button
              onClick={(e): void => {
                e.preventDefault();
                e.stopPropagation();
                onReplay?.(root);
              }}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-[6px] bg-[var(--flux-accent)]/10 hover:bg-[var(--flux-accent)]/20 text-[10px] font-mono uppercase text-[var(--flux-accent)] transition-all border border-[var(--flux-accent)]/20 shadow-lg"
            >
              <Play className="h-3 w-3 fill-current" /> Replay
            </button>
          </div>

          <span className="text-[9px] font-mono text-muted-foreground/40 truncate group-hover:hidden">
            {root.requestId}
          </span>
        </div>

        <div className="relative z-10 flex items-center hidden md:flex">
          <span className="text-[10px] text-muted-foreground/30 font-mono tracking-widest">
            {timeExact(root.receivedAt!)}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="px-4 py-3 bg-[#0d1117] border-y border-white/5 flex flex-col gap-1.5">
          <div className="text-[10px] font-mono text-muted-foreground/80 mb-2 px-2 py-1.5 border-l-2 border-white/20 bg-white/[0.015] rounded-r-[4px]">
            {requestStory}
          </div>

          {insights.length > 0 && (
            <>
              <span className="text-[9px] font-bold text-[var(--flux-warning)] tracking-widest uppercase mb-0.5 mt-0.5 flex items-center gap-1.5">
                <AlertTriangle className="h-3 w-3" /> Diagnostics
              </span>
              {insights.map((insight, idx): import('react/jsx-runtime').JSX.Element => (
                <div
                  key={idx}
                  className={cn(
                    'text-[10px] font-mono px-2.5 py-1.5 rounded-[4px] border flex items-start whitespace-pre-wrap leading-relaxed',
                    insight.type === 'cause'
                      ? 'bg-[var(--flux-error)]/10 border-[var(--flux-error)]/20 text-[var(--flux-error)]'
                      : insight.type === 'pattern'
                        ? 'bg-[var(--flux-error)]/5 border-[var(--flux-error)]/30 text-orange-400 font-semibold'
                        : insight.type === 'anomaly'
                          ? 'bg-purple-500/10 border-purple-500/20 text-purple-400'
                          : insight.type === 'suggestion'
                            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
                            : insight.type === 'critical' && root.status === 'error'
                              ? 'bg-[var(--flux-warning)]/10 border-[var(--flux-warning)]/30 text-[var(--flux-warning)]'
                              : 'bg-blue-500/10 border-blue-500/20 text-blue-400',
                  )}
                >
                  {insight.text}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {expanded && (
        <div className="p-3 bg-[#0d1117]/30">
          <div className="text-[9px] font-semibold text-muted-foreground/40 tracking-widest uppercase mb-2 px-3 flex items-center">
            <span className="w-[180px]">Trace Tree Stack</span>
            <div className="flex-1 flex justify-between mx-4 text-[8px] border-b border-white/5 pb-1">
              <span>0ms</span>
              <span>{Math.round(maxDurationMs * 0.25)}ms</span>
              <span>{Math.round(maxDurationMs * 0.5)}ms</span>
              <span>{Math.round(maxDurationMs * 0.75)}ms</span>
              <span>{Math.round(maxDurationMs)}ms</span>
            </div>
            <span className="w-[160px] px-3">Metadata Payload</span>
            <span className="w-[70px] text-right pr-2">Duration</span>
          </div>
          <div className="bg-[#111318] border border-white/5 rounded-md p-1.5 shadow-[inset_0_4px_20px_rgba(0,0,0,0.5)]">
            <TraceTimelineNode
              node={rootNode}
              rootStartedAt={rootReceivedAt}
              maxDurationMs={maxDurationMs}
              traceHasError={hasErrorInside}
              criticalPathNodeId={criticalPathNodeId}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const STEPS = [
  {
    icon: Terminal,
    color: 'text-[var(--flux-fetch)]',
    bg: 'bg-[var(--flux-fetch)]/10',
    title: 'Install the SDK',
    description: 'Add fluxrun to your project.',
    snippet: 'npm install fluxrun',
  },
  {
    icon: Key,
    color: 'text-[var(--flux-env)]',
    bg: 'bg-[var(--flux-env)]/10',
    title: 'Add your credentials',
    description: 'Copy FLUX_PROJECT_ID and FLUX_SECRET from your project settings into .env.',
    snippet: null,
    cta: { label: 'Go to Apps', href: '/projects' },
  },
  {
    icon: Zap,
    color: 'text-[var(--flux-accent)]',
    bg: 'bg-[var(--flux-accent)]/10',
    title: 'Wrap and run a function',
    description:
      'Wrap any async function with fluxFunc() and run it. It will appear here instantly.',
    snippet: 'export default fluxFunc(async function main() { … }, { name: "my-fn" })',
  },
];

function GettingStarted({
  hasProjects,
}: {
  hasProjects: boolean;
}): import('react/jsx-runtime').JSX.Element {
  return (
    <div className="rounded-xl border border-dashed p-8 space-y-8">
      <div className="text-center space-y-1">
        <div className="flex items-center justify-center gap-2 mb-3">
          <span className="h-8 w-8 rounded-full bg-[var(--flux-accent)]/10 flex items-center justify-center">
            <Zap className="h-4 w-4 text-[var(--flux-accent)]" />
          </span>
        </div>
        <h2 className="font-semibold text-base">Fire your first run</h2>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          Three steps and you&apos;ll see real-time execution traces right here.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {STEPS.map((step, i): import('react/jsx-runtime').JSX.Element => (
          <div key={i} className="space-y-3">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full shrink-0',
                  step.bg,
                )}
              >
                <step.icon className={cn('h-3.5 w-3.5', step.color)} />
              </span>
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                Step {i + 1}
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium">{step.title}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
            </div>

            {step.snippet && <CopySnippet text={step.snippet} />}
            {step.cta && (
              <Link
                to={step.cta.href}
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-full')}
              >
                {step.cta.label}
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Link>
            )}
          </div>
        ))}
      </div>

      {!hasProjects && (
        <div className="text-center">
          <Link to="/onboarding" className={cn(buttonVariants({ size: 'sm' }))}>
            Create your first app
            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Link>
        </div>
      )}
    </div>
  );
}

function Sparkline({
  data,
  color,
}: {
  data: number[];
  color: string;
}): import('react/jsx-runtime').JSX.Element | null {
  if (data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data);
  const range = max - min || 1;
  const width = 100;
  const height = 20;

  const points = data.map((d, i): string => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((d - min) / range) * height;
    return `${x},${y}`;
  });

  const path = `M ${points.join(' L ')}`;
  const areaPath = `${path} L ${width},${height} L 0,${height} Z`;

  return (
    <svg
      viewBox={`0 -4 ${width} ${height + 8}`}
      className="w-full h-8 overflow-visible"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.2" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#gradient-${color})`} />
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

import { useExecutionStore } from '@/lib/storage/provider';

export function ExecutionFeed({
  projects,
  suppressGettingStarted = false,
}: {
  projects: { id: string; name: string; agentUrl: string | null }[];
  /** When true, hide the large empty-state wizard (e.g. first-run setup shows above). */
  suppressGettingStarted?: boolean;
}): import('react/jsx-runtime').JSX.Element {
  const [selectedReplay, setSelectedReplay] = useState<ExecutionSummaryV2 | null>(null);

  const { executions, isLoading, error } = useExecutionStore();

  const apiError = error;

  const tree = useMemo((): ExecNode[] => buildExecutionTree(executions), [executions]);

  if (isLoading) {
    return (
      <div className="py-12 text-center text-muted-foreground animate-pulse font-mono text-xs tracking-widest">
        INITIALIZING COMMAND FEED...
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="rounded-xl border border-[var(--flux-error)]/20 bg-[var(--flux-error)]/5 p-4">
        <p className="text-sm font-medium text-[var(--flux-error)]">API Synchronization Failed</p>
        <p className="text-xs text-muted-foreground mt-1">{apiError}</p>
      </div>
    );
  }

  if (projects.length === 0) return <GettingStarted hasProjects={false} />;
  if (executions.length === 0 && !suppressGettingStarted)
    return <GettingStarted hasProjects={true} />;
  if (executions.length === 0 && suppressGettingStarted) {
    return (
      <div className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-muted-foreground">
        Run your instrumented app — executions will appear here as they arrive.
      </div>
    );
  }

  const successCount = executions.filter((e): boolean => e.status === 'success').length;
  const errorCount = executions.length - successCount;
  const successRate =
    executions.length > 0 ? Math.round((successCount / executions.length) * 100) : 0;
  const avgMs =
    executions.length > 0
      ? Math.round(
          executions.reduce((s, e): number => s + getExecutionDuration(e), 0) / executions.length,
        )
      : 0;

  const functionsMap = new Map<string, { count: number; totalMs: number; errors: number }>();
  for (const e of executions) {
    const entry = e.entry ?? 'unknown';
    const stat = functionsMap.get(entry) ?? { count: 0, totalMs: 0, errors: 0 };
    stat.count++;
    stat.totalMs += getExecutionDuration(e);
    if (e.status === 'error') stat.errors++;
    functionsMap.set(entry, stat);
  }
  const topFunctions = [...functionsMap.entries()]
    .map(([name, stat]) => ({
      name,
      shortName: shortEntry(name),
      avgMs: Math.round(stat.totalMs / stat.count),
      ...stat,
    }))
    .sort((a, b): number => b.count - a.count);

  const ongoingErrorPatterns = topFunctions
    .filter((f): boolean => f.errors > 1)
    .map((f): string => f.name);
  const topErrorFunction = [...topFunctions]
    .sort((a, b): number => b.errors - a.errors)
    .find((f): boolean => f.errors > 0);

  const errorExecs = executions.filter((e): boolean => e.status === 'error');

  const rootCause = ((): string | null => {
    const labelCounts = new Map<string, number>();
    for (const e of errorExecs) {
      const label = e.preview?.label;
      if (label && label !== 'unknown error')
        labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
    }
    const entries = [...labelCounts.entries()].sort((a, b): number => b[1] - a[1]);
    const topEntry = entries[0];
    return labelCounts.size === 0
      ? errorExecs.length > 0
        ? 'Upstream dependency failure'
        : null
      : (topEntry?.[0] ?? null);
  })();

  const mostRecentError = errorExecs.sort(
    (a, b): number =>
      new Date(b.completedAt ?? 0).getTime() - new Date(a.completedAt ?? 0).getTime(),
  )[0];
  const errorRecency =
    mostRecentError?.completedAt != null ? timeAgo(mostRecentError.completedAt) : null;

  return (
    <div className="space-y-4 animate-fade-in pb-12">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div
          className={cn(
            'relative group bg-[#0d1117] rounded-xl p-5 overflow-hidden border transition-all',
            errorCount > 0
              ? 'border-[var(--flux-error)]/40 shadow-[0_0_20px_var(--flux-error)]/[0.05]'
              : 'border-white/10',
          )}
        >
          <div
            className={cn(
              'absolute inset-0 bg-gradient-to-b to-transparent pointer-events-none',
              errorCount > 0 ? 'from-[var(--flux-error)]/5' : 'from-white/[0.01]',
            )}
          />
          <div className="relative flex items-center mb-4">
            <span
              className={cn(
                'px-2 py-0.5 rounded-[4px] text-[9px] font-mono tracking-widest uppercase border',
                errorCount > 0
                  ? 'bg-[var(--flux-error)]/20 text-[var(--flux-error)] border-[var(--flux-error)]/30'
                  : 'bg-[var(--flux-success)]/20 text-[var(--flux-success)] border-[var(--flux-success)]/30',
              )}
            >
              {errorCount > 0 ? 'Anomaly Detected' : 'System Healthy'}
            </span>
            {errorCount > 0 ? (
              <AlertTriangle className="ml-2 h-4 w-4 text-[var(--flux-error)]" />
            ) : (
              <CheckCircle2 className="ml-2 h-4 w-4 text-[var(--flux-success)]" />
            )}
          </div>

          <h3 className="relative font-mono text-lg font-medium text-foreground mb-6">
            {errorCount > 0
              ? `${shortEntry(topErrorFunction?.name ?? 'unknown')} failing repeatedly`
              : 'No critical failures detected'}
          </h3>

          <div className="relative grid grid-cols-3 gap-4">
            <div className="bg-[#1a1b1e] rounded-[8px] p-3 border border-white/5">
              <div className="text-[9px] text-muted-foreground uppercase tracking-widest mb-1.5">
                Frequency
              </div>
              <div className="font-semibold text-lg">
                {errorCount}{' '}
                <span className="text-xs font-normal text-muted-foreground/60">fails</span>
              </div>
            </div>
            <div className="bg-[#1a1b1e] rounded-[8px] p-3 border border-white/5">
              <div className="text-[9px] text-muted-foreground uppercase tracking-widest mb-1.5">
                Primary Root
              </div>
              <div className="font-medium text-xs text-[var(--flux-error)] truncate">
                {rootCause ?? 'N/A'}
              </div>
            </div>
            <div className="bg-[#1a1b1e] rounded-[8px] p-3 border border-white/5">
              <div className="text-[9px] text-muted-foreground uppercase tracking-widest mb-1.5">
                Last Seen
              </div>
              <div className="font-semibold text-lg">{errorRecency ?? '--'}</div>
            </div>
          </div>
        </div>

        {errorCount === 0 ? (
          <div className="relative bg-[#0d1117] border border-white/10 rounded-xl p-5 flex flex-col min-h-[180px] overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[var(--flux-accent)]/[0.05] to-transparent pointer-events-none" />
            <div className="relative flex items-center gap-2 mb-4">
              <Zap className="h-4 w-4 text-[var(--flux-accent)] animate-pulse" />
              <span className="text-[10px] font-mono tracking-widest uppercase text-foreground/50">
                Fidelity Protocol Active
              </span>
              <div className="flex-1" />
              <span className="px-2 py-0.5 rounded-[4px] bg-[var(--flux-accent)]/10 text-[var(--flux-accent)] text-[9px] font-mono tracking-widest uppercase border border-[var(--flux-accent)]/20">
                Deterministic
              </span>
            </div>
            <div className="relative flex-1 flex flex-col items-center justify-center text-center gap-1.5">
              <p className="text-sm font-bold text-foreground">SYSTEM STEADY — 100% REPLAYABLE</p>
              <p className="text-xs text-muted-foreground/60 leading-relaxed max-w-[280px]">
                FluxBase is actively recording. Every stable execution is ready for deterministic
                exploration.
              </p>
              <div className="mt-2 text-[9px] text-[var(--flux-accent)] uppercase tracking-widest bg-[var(--flux-accent)]/10 px-4 py-1.5 rounded-full border border-[var(--flux-accent)]/20 font-bold">
                Control Determinism → Press Replay
              </div>
            </div>
          </div>
        ) : (
          <div className="relative bg-[#0d1117] border border-[var(--flux-warning)]/30 rounded-xl p-5 overflow-hidden">
            <div className="text-[9px] font-mono uppercase tracking-widest text-[var(--flux-warning)] mb-4">
              Performance Impact
            </div>
            <div className="grid grid-cols-2 gap-4 h-full">
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase mb-1">
                    Success Rate
                  </div>
                  <div className="text-2xl font-bold tracking-tighter">{successRate}%</div>
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground uppercase mb-1">
                    Avg Duration
                  </div>
                  <div className="text-2xl font-bold tracking-tighter">{avgMs}ms</div>
                </div>
              </div>
              <div className="flex items-end h-full pb-4">
                <Sparkline
                  data={executions
                    .map((e): number => getExecutionDuration(e))
                    .reverse()
                    .slice(0, 30)}
                  color="var(--flux-warning)"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4 px-2">
          <h2 className="text-xl font-bold tracking-tight text-foreground">
            Live Replayable Executions
          </h2>
          <div className="flex items-center gap-4 text-[10px] font-mono tracking-widest uppercase text-muted-foreground/40">
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--flux-success)]" /> Stable
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--flux-error)]" /> Failed
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {tree.map((node): import('react/jsx-runtime').JSX.Element => (
            <TraceCard
              key={node.exec.executionId}
              rootNode={node}
              ongoingErrorPatterns={ongoingErrorPatterns}
              onReplay={(exec): void => setSelectedReplay(exec)}
            />
          ))}
        </div>
      </div>

      <ReplayDialog
        open={!!selectedReplay}
        onOpenChange={(open): false | void => !open && setSelectedReplay(null)}
        projectId={selectedReplay?.projectId ?? ''}
        executionId={selectedReplay?.executionId ?? ''}
        productionUrl={
          selectedReplay
            ? (projects.find((p): boolean => p.id === selectedReplay.projectId)?.agentUrl ?? null)
            : null
        }
        originalDurationMs={selectedReplay ? getExecutionDuration(selectedReplay) : 0}
      />
    </div>
  );
}
